import { Button, Flex } from '@aws-amplify/ui-react';
import type { Edulead } from 'edulead';
import { useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { TokenPagination } from '../components/TokenPagination';
import ConfirmModal from '../custom-components/ConfirmModal';
import ExportsTable, { Export } from '../custom-components/ExportsTable';
import LoadingBars from '../custom-components/LoadingBars';
import NothingHere from '../custom-components/NothingHere';
import { GlobalContext, GlobalContextProps } from '../global-context';
import { useCopyExport } from '../hooks/useCopyExport';
import { useMemberEmails } from '../hooks/useMemberEmails';
import { enumLabel, toastV4Error, useTokenPager } from '../services/edulead-v4';
import { eduleadV4 } from '../services/edulead-v4-client';
import General from '../utils/GeneralUtils';
import { exportViewOf } from './export-steps/exportShapes';

const PAGE_SIZE = 50;

/**
 * A Go API export as a row of the shared ExportsTable, which boss still fills from Nest's list
 * (every team's exports, which `/v4` does not serve). The creator's email comes from the members.
 */
function tableRowOf(e: Edulead.EduleadV4Export, memberEmails: Map<string, string>): Export {
  const view = exportViewOf(e);
  const provider = enumLabel(e.options?.email_provider, 'EXPORT_EMAIL_PROVIDER_');
  return {
    id: e.id ?? '',
    created_at: e.create_time ?? '',
    completed_at: e.complete_time,
    entity: view.entity,
    target: view.target || undefined,
    version: e.version || 1,
    user_email: memberEmails.get(e.creator_id ?? '') || undefined,
    record_count: e.record_count ?? undefined,
    success_count: e.success_count ?? undefined,
    failed_count: e.failed_count ?? undefined,
    skipped_count: e.skipped_count ?? undefined,
    status: view.status,
    error: e.failure_message || undefined,
    file_name: e.file_name || undefined,
    has_file: !!e.file_name,
    metadata: provider ? { revalidateEmailsProvider: provider } : undefined,
    stats: e.hubspot_list_id ? { hubspotListId: e.hubspot_list_id, hubspotPortalId: e.hubspot_portal_id } : undefined,
  };
}

export function ExportsPage(props: any) {

  const globalContext = useContext(GlobalContext);
  General.setTitle(document, 'Exports', globalContext);

  const memberEmails = useMemberEmails();
  const { copyExport } = useCopyExport();
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [exportToArchive, setExportToArchive] = useState<Export | null>(null);
  const [isArchiving, setIsArchiving] = useState(false);

  const pager = useTokenPager(
    async (pageToken?: string) => {
      const page = await eduleadV4.exports.list({ page_size: PAGE_SIZE, page_token: pageToken });
      return { data: page.data, response: page.response };
    },
    // Once: the token names the team, so the list need not wait for (or reload on) the auth info
    [],
    'Failed to load exports'
  );
  const exports = (pager.items ?? []).map((e) => tableRowOf(e, memberEmails));

  async function onDownloadExportFile(ex: Export) {
    try {
      // GetExport signs a fresh link for a successful file export
      const { export: current } = await eduleadV4.exports.get({ id: ex.id })
      if (!current?.download_uri) {
        throw new Error('Failed to download export')
      }
      await General.downloadUrl(current.download_uri, current.file_name ?? '')
    } catch (error) {
      toastV4Error(error, 'Failed to download export')
    }
  }

  function handleArchive(ex: Export) {
    setExportToArchive(ex);
    setArchiveModalOpen(true);
  }

  async function confirmArchive() {
    if (!exportToArchive) return;
    try {
      setIsArchiving(true);
      await eduleadV4.exports.archive({ id: exportToArchive.id });
      toast.success('Export archived successfully');
      setArchiveModalOpen(false);
      setExportToArchive(null);
      pager.reload();
    } catch (error) {
      toastV4Error(error, 'Failed to archive export');
    } finally {
      setIsArchiving(false);
    }
  }

  return <GlobalContext.Consumer>
    {
      (globalContextProps: GlobalContextProps) => <>
        <ConfirmModal
          isOpen={archiveModalOpen}
          onClose={() => {
            setArchiveModalOpen(false);
            setExportToArchive(null);
          }}
          onConfirm={confirmArchive}
          title="Archive Export"
          message="Are you sure you want to archive this export? Archived exports will be hidden from the list."
          confirmText="Archive"
          isLoading={isArchiving}
          loadingText="Archiving..."
        />
        <div className="content content-1200">
          <Flex justifyContent="flex-end" alignItems="center" style={{ marginBottom: 20 }}>
            <Button
              size='small'
              className='tiny'
              onClick={event => pager.reload()}
            >
              Refresh
            </Button>
          </Flex>
          {
            pager.loading
              ? <LoadingBars numberOfRows={20} />
              : exports.length > 0
                  ? <>
                      <ExportsTable
                        exports={exports}
                        onDownload={onDownloadExportFile}
                        onArchive={handleArchive}
                        onCopy={(ex) => copyExport(ex.id)}
                      />
                      <Flex justifyContent='center'>
                        <TokenPagination
                          page={pager.page}
                          pageSize={PAGE_SIZE}
                          shown={exports.length}
                          hasNext={pager.hasNext}
                          isLoading={pager.loading}
                          onPrevious={pager.prev}
                          onNext={pager.next}
                        />
                      </Flex>
                    </>
                  : <NothingHere text="No exports found" />
          }
        </div>
      </>
    }
  </GlobalContext.Consumer>
}
